import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { ShieldAlert, RefreshCcw, Loader2, Unlock } from "lucide-react";
import { api } from "@/services/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

/**
 * Historial de bloqueos activos.
 *
 * Muestra cuentas (admin o socio) actualmente bloqueadas por rate limit
 * y permite a Gerencia/Sistemas liberarlas manualmente con un motivo
 * obligatorio. La lista se hidrata desde `GET /api/auth/bloqueos-activos`.
 */
export default function AdminBloqueos() {
  const [items, setItems] = useState([]);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState("");
  const [seleccion, setSeleccion] = useState(null);
  const [motivo, setMotivo] = useState("");
  const [desbloqueando, setDesbloqueando] = useState(false);

  const hidratar = useCallback(async () => {
    setCargando(true);
    setError("");
    try {
      const { data } = await api.get("/auth/bloqueos-activos");
      setItems(Array.isArray(data?.items) ? data.items : []);
    } catch (err) {
      const s = err?.response?.status;
      if (s === 403) setError("Sólo Gerencia y Sistemas pueden ver esta lista.");
      else setError("No se pudo cargar el historial de bloqueos.");
      setItems([]);
    } finally {
      setCargando(false);
    }
  }, []);

  useEffect(() => {
    hidratar();
  }, [hidratar]);

  const abrirDesbloqueo = (item) => {
    setSeleccion(item);
    setMotivo("");
  };

  const cerrarDialog = () => {
    if (desbloqueando) return;
    setSeleccion(null);
    setMotivo("");
  };

  const confirmar = async () => {
    if (!seleccion) return;
    const trimmed = motivo.trim();
    if (trimmed.length < 3) {
      toast.error("Escribe un motivo (mínimo 3 caracteres).");
      return;
    }
    setDesbloqueando(true);
    try {
      await api.post("/auth/desbloquear", {
        subjectType: seleccion.subjectType,
        identifier: seleccion.identifier,
        motivo: trimmed,
      });
      toast.success(`Desbloqueado: ${seleccion.identifier}`);
      setSeleccion(null);
      setMotivo("");
      await hidratar();
    } catch (err) {
      const detail = err?.response?.data?.detail || "No se pudo desbloquear.";
      toast.error(detail);
    } finally {
      setDesbloqueando(false);
    }
  };

  const total = items.length;
  const titulo = useMemo(
    () => (total > 0 ? `${total} cuenta${total === 1 ? "" : "s"} bloqueada${total === 1 ? "" : "s"}` : "Sin cuentas bloqueadas"),
    [total],
  );

  const formatearFecha = (iso) => {
    if (!iso) return "—";
    try {
      const d = new Date(iso);
      return d.toLocaleString("es-MX", {
        day: "2-digit",
        month: "short",
        hour: "2-digit",
        minute: "2-digit",
      });
    } catch {
      return iso;
    }
  };

  return (
    <div data-testid="admin-bloqueos" className="flex flex-col gap-6">
      <header className="flex items-start justify-between gap-4">
        <div>
          <span className="overline text-accent inline-flex items-center gap-2">
            <ShieldAlert className="w-4 h-4" />
            Seguridad
          </span>
          <h1 className="mt-2 font-heading text-3xl tracking-tight text-foreground">
            Historial de bloqueos
          </h1>
          <p
            data-testid="bloqueos-titulo"
            className="text-sm text-muted-foreground mt-1"
          >
            {titulo}. Bloqueo automático tras 5 fallos en 15 minutos.
          </p>
        </div>
        <Button
          variant="outline"
          onClick={hidratar}
          disabled={cargando}
          data-testid="bloqueos-refrescar"
        >
          <RefreshCcw className="w-4 h-4 mr-2" />
          Refrescar
        </Button>
      </header>

      {error ? (
        <div
          data-testid="bloqueos-error"
          className="rounded-xl border border-destructive/40 bg-destructive/10 text-destructive px-4 py-3 text-sm"
        >
          {error}
        </div>
      ) : cargando ? (
        <div className="flex items-center gap-3 text-muted-foreground text-sm">
          <Loader2 className="w-4 h-4 animate-spin" />
          Cargando bloqueos activos…
        </div>
      ) : items.length === 0 ? (
        <div
          data-testid="bloqueos-vacio"
          className="rounded-2xl border hairline bg-card px-6 py-10 text-center text-muted-foreground"
        >
          Ninguna cuenta se encuentra bloqueada por rate limit en este momento.
        </div>
      ) : (
        <ul
          data-testid="bloqueos-lista"
          className="rounded-2xl border hairline bg-card overflow-hidden divide-y hairline"
        >
          {items.map((it) => (
            <li
              key={`${it.subjectType}:${it.identifier}`}
              data-testid={`bloqueo-item-${it.subjectType}-${it.identifier}`}
              className="flex flex-wrap md:flex-nowrap items-center gap-4 px-5 py-4"
            >
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="inline-flex items-center rounded-full bg-muted px-2 py-0.5 text-xs uppercase text-muted-foreground">
                    {it.subjectType === "socio" ? "Socio" : "Admin"}
                  </span>
                  <span className="font-medium text-foreground truncate">
                    {it.identifier}
                  </span>
                </div>
                <p className="mt-1 text-xs text-muted-foreground">
                  Último intento: {formatearFecha(it.ultimoIntento)} · Fallos:
                  {" "}
                  {it.fallos} · Vence: {formatearFecha(it.bloqueadoHasta)}
                </p>
              </div>
              <Button
                variant="outline"
                onClick={() => abrirDesbloqueo(it)}
                data-testid={`bloqueo-desbloquear-${it.subjectType}-${it.identifier}`}
              >
                <Unlock className="w-4 h-4 mr-2" />
                Desbloquear
              </Button>
            </li>
          ))}
        </ul>
      )}

      <Dialog open={!!seleccion} onOpenChange={(o) => !o && cerrarDialog()}>
        <DialogContent data-testid="bloqueo-dialog">
          <DialogHeader>
            <DialogTitle>Desbloquear cuenta</DialogTitle>
            <DialogDescription>
              {seleccion
                ? `Se liberarán los intentos fallidos recientes de ${seleccion.subjectType}:${seleccion.identifier}. Registraré tu nombre, hora y motivo en la bitácora.`
                : ""}
            </DialogDescription>
          </DialogHeader>
          <div className="flex flex-col gap-2">
            <label
              htmlFor="motivo-desbloqueo"
              className="text-sm font-medium text-foreground"
            >
              Motivo (obligatorio)
            </label>
            <Textarea
              id="motivo-desbloqueo"
              data-testid="bloqueo-motivo"
              rows={3}
              placeholder="Ej. Socio confirmó su identidad por teléfono con Recepción."
              value={motivo}
              onChange={(e) => setMotivo(e.target.value)}
              disabled={desbloqueando}
              className="bg-background resize-none"
            />
          </div>
          <DialogFooter>
            <Button
              variant="ghost"
              onClick={cerrarDialog}
              disabled={desbloqueando}
              data-testid="bloqueo-cancelar"
            >
              Cancelar
            </Button>
            <Button
              onClick={confirmar}
              disabled={desbloqueando || motivo.trim().length < 3}
              data-testid="bloqueo-confirmar"
            >
              {desbloqueando ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Desbloqueando…
                </>
              ) : (
                <>
                  <Unlock className="w-4 h-4 mr-2" />
                  Confirmar desbloqueo
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
